/* @flow */

import { useFocusWithin, useId } from 'react-aria';
import * as React from 'react';
import classNames from 'classnames';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

// Styles
import styles from './ProfileMenu.style';
import createUseThemeStyles from 'src/shared/app/base/util/createUseThemeStyles';

// Utils
import messages from 'src/shared/app/nav/l10n/navigationL10n.json';
import useRoutes from 'src/shared/app/base/route/hook/useRoutes';
import { getSelf } from 'src/shared/domain/user/selector/userSelectors';
import { handleLogOut } from 'src/shared/app/authentication/util/authenticationUtils';
import { TOUR_SELECTOR_KEYS } from 'src/shared/app/coachmark/config/tourConfig';
import { userEditInterestsSectionId } from 'src/shared/components/domain/user/constants/userConstants';
import {
  parseFirstName,
  getUserFullName,
} from '@braindate/domain/lib/user/util';
import {
  preferencesRoute,
  profileRoute,
  ownTopicsRoute,
  availabilityRoute,
  profileEditRoute,
} from 'src/shared/app/base/route/setting/routeSettings';

// Components
import Tooltip from '@mui/material/Tooltip';
import MenuPopover from 'src/shared/ui/component/menu/MenuPopover';
import MenuPopoverItem from 'src/shared/ui/component/menu/MenuPopoverItem';
import UserAvatar, {
  xsmall,
} from 'src/shared/components/domain/user/avatar/UserAvatar';

// Icons
import CogIcon from 'src/shared/ui/component/icon/CogIcon';
import LogOutIcon from 'src/shared/ui/component/icon/LogOutIcon';
import CalendarIcon from 'src/shared/ui/component/icon/CalendarIcon2';
import InterestsIcon from 'src/shared/ui/component/icon/InterestsIcon';
import MyTopicsIcon from 'src/shared/ui/component/icon/MyTopicsIcon';

type Props = {
  children: React.Node,
  isMenuOpened: boolean,
  setIsMenuOpened: (newValue: boolean) => void,
  setIsFocused: (isFocused: boolean) => void,
};

type MenuItemType = {
  icon: React.Node,
  title: string,
  subtitle?: string,
  to?: string,
  className?: string,
  onClick?: () => void,
};

const useStyles = createUseThemeStyles(styles);

const ProfileMenu = ({
  children,
  isMenuOpened,
  setIsMenuOpened,
  setIsFocused,
}: Props): React.Node => {
  /*
  |----------------------------------------------------------------------------
  | Hooks
  |----------------------------------------------------------------------------
  */
  const classes = useStyles();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const user = useSelector(getSelf);
  const userName = parseFirstName(user);

  const anchorEl = React.useRef();

  const [
    preferencesPath,
    profilePath,
    ownTopicsPath,
    profileEditPath,
    availabilityPath,
  ] = useRoutes(
    preferencesRoute,
    profileRoute,
    ownTopicsRoute,
    profileEditRoute,
    availabilityRoute,
  );

  const intl = useIntl();
  const menuId = useId();

  /*
   |----------------------------------------------------------------------------
   | Actions
   |----------------------------------------------------------------------------
   */

  const handleLogout = () => dispatch(handleLogOut());

  const { focusWithinProps } = useFocusWithin({
    onFocusWithin: () => setIsFocused(true),
    onBlurWithin: () => setIsFocused(false),
  });

  /*
  |------------------------------------------------------------------------------
  | Effects
  |------------------------------------------------------------------------------
  */

  React.useEffect(() => {
    setIsMenuOpened(false);
  }, [pathname]);

  /*
   |----------------------------------------------------------------------------
   | Elements
   |----------------------------------------------------------------------------
   */

  const menuItems: MenuItemType[] = [
    {
      icon: user ? <UserAvatar user={user} variant={xsmall} /> : user,
      title: getUserFullName(user),
      subtitle: intl.formatMessage(messages.myProfile),
      className: classes.profile,
      to: profilePath,
      hasDivider: true,
    },
    {
      icon: <MyTopicsIcon />,
      title: intl.formatMessage(messages.myTopics),
      to: ownTopicsPath,
    },
    {
      icon: <InterestsIcon />,
      title: intl.formatMessage(messages.myInterests),
      to: `${profileEditPath}#${userEditInterestsSectionId}`,
    },
    {
      icon: <CalendarIcon />,
      title: intl.formatMessage(messages.myAvailabilities),
      to: availabilityPath,
    },
    {
      icon: <CogIcon />,
      title: intl.formatMessage(messages.accountSettings),
      to: preferencesPath,
    },
    {
      icon: <LogOutIcon />,
      title: intl.formatMessage(messages.logOut),
      onClick: handleLogout,
    },
  ];

  return (
    <>
      <div
        className={classNames(
          classes.toggleButtonContainer,
          TOUR_SELECTOR_KEYS.PROFILE_BUTTON,
        )}
        {...focusWithinProps}
      >
        <Tooltip title={intl.formatMessage(messages.profileMenu)} arrow>
          <button
            className={classes.toggleButton}
            type="button"
            ref={anchorEl}
            onClick={() => setIsMenuOpened(true)}
            aria-controls={isMenuOpened ? menuId : null}
            aria-haspopup="menu"
            aria-label={userName}
          >
            {children}
          </button>
        </Tooltip>
      </div>
      <MenuPopover
        id={menuId}
        anchorEl={anchorEl.current && anchorEl.current}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        getContentAnchorEl={null}
        open={isMenuOpened}
        onClose={() => setIsMenuOpened(false)}
      >
        {menuItems.map((item, key) => (
          <MenuPopoverItem
            {...item}
            isActive={item.to ? pathname === item.to : false}
            key={key}
          />
        ))}
      </MenuPopover>
    </>
  );
};

export default React.memo<Props>(ProfileMenu);
